// @ts-nocheck
import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Box,
  IconButton,
  Tooltip,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import Iconify from '../iconify';
import { TopUser } from './types';
import { fShortenNumber } from '../../utils/formatNumber';

interface TopUsersTableProps {
  title: string;
  subheader?: string;
  users: TopUser[];
  onUserClick: (username: string) => void;
}

export default function TopUsersTable({ title, subheader, users, onUserClick }: TopUsersTableProps) {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDownload = () => {
    const headers = ['#', 'User', 'Queries', 'Tokens', 'Cost', 'Last Active'];
    const rows = users.map((user, i) => [
      i + 1,
      user.username,
      user.totalQueries,
      user.totalTokens,
      `$${user.totalCost.toFixed(2)}`,
      user.lastActive,
    ]);
    const csv = [headers, ...rows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'users_list.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const paginatedUsers = users.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Card>
      <CardHeader
        title={title}
        subheader={subheader}
        action={
          <Tooltip title="Download CSV">
            <IconButton onClick={handleDownload}>
              <Iconify icon="mdi:download" />
            </IconButton>
          </Tooltip>
        }
      />
      <TableContainer sx={{ px: 2, overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 500 }}>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>User</TableCell>
              <TableCell align="right">Queries</TableCell>
              <TableCell align="right">Tokens</TableCell>
              <TableCell align="right">Cost</TableCell>
              <TableCell align="right">Last Active</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedUsers.map((user, index) => {
              const globalIndex = page * rowsPerPage + index;
              return (
                <TableRow
                  key={user.username}
                  hover
                  onClick={() => onUserClick(user.username)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.primary.main, 0.08),
                    },
                  }}
                >
                  <TableCell>
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor:
                          globalIndex < 3
                            ? [theme.palette.primary.main, theme.palette.info.main, theme.palette.warning.main][
                                globalIndex
                              ]
                            : theme.palette.grey[300],
                        color: globalIndex < 3 ? '#fff' : theme.palette.text.secondary,
                        fontSize: 12,
                        fontWeight: 700,
                      }}
                    >
                      {globalIndex + 1}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="subtitle2">{user.username}</Typography>
                  </TableCell>
                  <TableCell align="right">{user.totalQueries}</TableCell>
                  <TableCell align="right">{fShortenNumber(user.totalTokens)}</TableCell>
                  <TableCell align="right">${user.totalCost.toFixed(2)}</TableCell>
                  <TableCell align="right">
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {new Date(user.lastActive).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={users.length}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[10, 25, 50]}
      />
    </Card>
  );
}
